import { useEffect } from "react";
import { updateCanvasStoryboards } from "@/store/supabaseSlice";
import { useActiveCanvas } from "@/utils/utils";
import { useSession } from "@clerk/clerk-react";
import { Storyboard } from "@/utils/types";
import { StoryboardDelta } from "@/store/zustand/changeLedgerTypes";
import { track } from "@/utils/events/tracker";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";

const isValidStoryboard = (storyboard: Storyboard) => {
  return (
    storyboard &&
    typeof storyboard === "object" &&
    storyboard.x !== undefined &&
    storyboard.y !== undefined &&
    (storyboard.width || 0) > 0 &&
    (storyboard.height || 0) > 0 &&
    storyboard.id !== undefined
  );
};

export const usePruneInactiveStoryboards = () => {
  const { session } = useSession();

  const canvas = useActiveCanvas();
  const INACTIVE_PERIOD = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

  useEffect(() => {
    const prune = async () => {
      const storyboardsToDelete: Storyboard[] = [];
      const allKnownStoryboardIds = new Set<string>();

      if (canvas?.storyboards) {
        // Collect invalid or permanently deleted storyboards for this canvas
        canvas.storyboards.forEach((storyboard) => {
          if (storyboard?.id) {
            allKnownStoryboardIds.add(storyboard.id);
          }

          if (!isValidStoryboard(storyboard)) {
            storyboardsToDelete.push(storyboard);
            return;
          }

          if (!storyboard.deleted) {
            return;
          }

          const deletedAt = storyboard.deleted_at;
          if (!deletedAt) {
            storyboardsToDelete.push(storyboard);
            return;
          }

          // deleted_at is either a
          // a) Firestore Timestamp object (with _seconds and _nanoseconds properties)
          // b) An ISO string
          let deletedAtDate;

          if (deletedAt._seconds) {
            deletedAtDate = new Date(
              deletedAt._seconds * 1000 + deletedAt._nanoseconds / 1000000,
            );
          } else {
            deletedAtDate = new Date(deletedAt);
          }

          const now = new Date();
          const inactiveThreshold = new Date(now.getTime() - INACTIVE_PERIOD);

          const isInactive = deletedAtDate < inactiveThreshold;

          if (isInactive) {
            storyboardsToDelete.push(storyboard);
          }
        });

        if (storyboardsToDelete.length) {
          const storyboardDeltas: StoryboardDelta[] = storyboardsToDelete.map(
            (sb) => ({
              changeType: "remove",
              storyboardId: sb.id,
              pageId: sb.pageId || "",
              storyboardUpdateDict: {},
            }),
          );

          await updateCanvasStoryboards(canvas.id, storyboardDeltas);
        }
      }

      // Remove saved HTML that no longer belongs to the active canvas or is stale
      const storyboardSavedHtmls =
        useSupabaseStore.getState().savedStoryboardHtml;
      const savedStoryboardHtmlsIdsToDelete: string[] = [];
      const oneWeekAgoMs = Date.now() - INACTIVE_PERIOD;

      for (const storyboardSavedHtml of Object.values(storyboardSavedHtmls)) {
        const storyboardId = storyboardSavedHtml?.storyboard_id;
        if (!storyboardId) {
          continue;
        }

        if (storyboardSavedHtml.canvas_id === canvas?.id) {
          if (!allKnownStoryboardIds.has(storyboardId)) {
            savedStoryboardHtmlsIdsToDelete.push(storyboardId);
          }
          continue;
        }

        // Prune old storyboards across all canvases since a canvas may never be opened again.
        const lastFrozenMs = storyboardSavedHtml.last_frozen_at
          ? new Date(storyboardSavedHtml.last_frozen_at).getTime()
          : undefined;
        const createdMs = storyboardSavedHtml.created_at
          ? new Date(storyboardSavedHtml.created_at).getTime()
          : undefined;
        const referenceTimeMs = lastFrozenMs ?? createdMs;

        if (referenceTimeMs !== undefined && referenceTimeMs < oneWeekAgoMs) {
          savedStoryboardHtmlsIdsToDelete.push(storyboardId);
        }
      }

      if (savedStoryboardHtmlsIdsToDelete.length) {
        await useSupabaseStore
          .getState()
          .supabaseClient?.from("saved_storyboard_html")
          .delete()
          .in("storyboard_id", savedStoryboardHtmlsIdsToDelete);
      }
    };

    if (session && canvas?.storyboards) {
      prune();
    }
  }, [canvas?.storyboards, session, canvas?.id, INACTIVE_PERIOD]);
};
